/**
 * 问卷重新提交模态框
 * 支持查看驳回原因、填写变更说明、重新提交问卷
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  Alert,
  Divider,
  Timeline,
  Tag,
  Space,
  Spin,
  Typography,
  Card,
  Row,
  Col,
  Collapse,
  Descriptions,
} from 'antd';
import {
  ExclamationCircleOutlined,
  EditOutlined,
  SendOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import {
  getLargeModalProps,
  getUnifiedModalTitle,
  getCardStyle,
  getCardTitleStyle,
  getPrimaryButtonStyle,
  getSecondaryButtonStyle,
  getCancelButtonStyle,
  getScrollableContentStyle,
  getAlertStyle,
  emeraldColors,
} from '../../styles/modalStyles';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

interface ReviewHistory {
  questionnaireId: string;
  projectName: string;
  currentVersion: number;
  currentStatus: string;
  totalSubmissions: number;
  versions: Array<{
    version: number;
    status: string;
    submittedAt: string;
    reviewedAt?: string;
    reviewResult?: string;
    changeDescription?: string;
    changedFieldsCount: number;
    changedFields: string[];
    reviewRecord?: {
      reviewerName: string;
      decision: string;
      comments: string;
      rejectionReasons: string[];
      aiScore?: number;
      riskLevel?: string;
      reviewedAt: string;
    };
  }>;
  timeline: Array<{
    version: number;
    event: string;
    timestamp: string;
    actor: string;
    actorType: string;
    description: string;
    details?: any;
  }>;
  statistics: {
    totalVersions: number;
    rejectedCount: number;
    approvedCount: number;
    pendingCount: number;
    averageReviewTime?: number;
    lastActivity: string;
  };
}

interface QuestionnaireResubmissionModalProps {
  visible: boolean;
  onCancel: () => void;
  questionnaireId: string;
  projectName: string;
  onResubmitSuccess: () => void;
}

export const QuestionnaireResubmissionModal: React.FC<
  QuestionnaireResubmissionModalProps
> = ({
  visible,
  onCancel,
  questionnaireId,
  projectName,
  onResubmitSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [reviewHistory, setReviewHistory] = useState<ReviewHistory | null>(
    null
  );
  const [activeTab, setActiveTab] = useState<'review' | 'history'>('review');

  // 获取审核历史
  const fetchReviewHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await fetch(
        `/api/v1/questionnaires/${questionnaireId}/history`
      );
      const result = await response.json();

      if (result.success) {
        setReviewHistory(result.data.history);
      } else {
        console.error('获取审核历史失败:', result.error);
      }
    } catch (error) {
      console.error('获取审核历史失败:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (visible && questionnaireId) {
      fetchReviewHistory();
      form.resetFields();
    }
  }, [visible, questionnaireId, form]);

  // 处理重新提交
  const handleResubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // 这里应该调用实际的重新提交API
      // 暂时模拟API调用
      await new Promise(resolve => setTimeout(resolve, 2000));

      console.log('重新提交问卷:', {
        questionnaireId,
        changeDescription: values.changeDescription,
      });

      onResubmitSuccess();
      onCancel();
    } catch (error) {
      console.error('重新提交失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 获取状态图标
  const getStatusIcon = (status: string, result?: string) => {
    if (status === 'submitted' && !result) {
      return <ClockCircleOutlined style={{ color: '#1890ff' }} />;
    }
    if (result === 'approved') {
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    }
    if (result === 'rejected') {
      return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
    }
    return <InfoCircleOutlined style={{ color: '#d9d9d9' }} />;
  };

  // 获取状态颜色
  const getStatusColor = (status: string, result?: string) => {
    if (status === 'submitted' && !result) return 'processing';
    if (result === 'approved') return 'success';
    if (result === 'rejected') return 'error';
    return 'default';
  };

  // 渲染最新驳回信息
  const renderLatestRejection = () => {
    if (!reviewHistory) return null;

    const latestRejected = reviewHistory.versions.find(
      v => v.reviewResult === 'rejected'
    );
    if (!latestRejected?.reviewRecord) return null;

    const { reviewRecord } = latestRejected;

    return (
      <Card
        title={getCardTitleStyle(
          <CloseCircleOutlined />,
          `最新审核意见 - 第${latestRejected.version}版被驳回`
        )}
        size="small"
        style={getCardStyle({ borderColor: 'rgba(239, 68, 68, 0.3)' })}
        styles={{ body: { padding: '16px' } }}
      >
        <Descriptions column={2} size="small">
          <Descriptions.Item label="审核时间">
            {new Date(reviewRecord.reviewedAt).toLocaleString('zh-CN')}
          </Descriptions.Item>
          <Descriptions.Item label="审核人员">
            {reviewRecord.reviewerName}
          </Descriptions.Item>
          <Descriptions.Item label="AI评分">
            <Tag
              color={
                reviewRecord.aiScore && reviewRecord.aiScore >= 80
                  ? 'green'
                  : 'orange'
              }
            >
              {reviewRecord.aiScore || 'N/A'}分
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="风险等级">
            <Tag color={reviewRecord.riskLevel === 'low' ? 'green' : 'orange'}>
              {reviewRecord.riskLevel || 'N/A'}
            </Tag>
          </Descriptions.Item>
        </Descriptions>

        <Divider />

        <div style={{ marginBottom: '16px' }}>
          <Text strong>审核意见：</Text>
          <div
            style={{
              marginTop: '8px',
              padding: '12px',
              background: '#fff2e8',
              border: '1px solid #ffcc02',
              borderRadius: '6px',
              lineHeight: '1.6',
            }}
          >
            {reviewRecord.comments || '暂无具体意见'}
          </div>
        </div>

        {reviewRecord.rejectionReasons &&
          reviewRecord.rejectionReasons.length > 0 && (
            <div>
              <Text strong>具体驳回原因：</Text>
              <div style={{ marginTop: '8px' }}>
                {reviewRecord.rejectionReasons.map((reason, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                      marginBottom: '8px',
                      padding: '12px',
                      background: '#fff1f0',
                      border: '1px solid #ffccc7',
                      borderRadius: '6px',
                    }}
                  >
                    <ExclamationCircleOutlined
                      style={{ color: '#ff4d4f', marginTop: '2px' }}
                    />
                    <Text>{reason}</Text>
                  </div>
                ))}
              </div>
            </div>
          )}
      </Card>
    );
  };

  // 渲染审核历史
  const renderReviewHistory = () => {
    if (!reviewHistory) return null;

    return (
      <div>
        {/* 统计信息 */}
        <Card
          title={getCardTitleStyle(<HistoryOutlined />, '审核统计')}
          size="small"
          style={getCardStyle()}
          styles={{ body: { padding: '16px' } }}
        >
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <div style={{ textAlign: 'center' }}>
                <div
                  style={{
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: '#1890ff',
                  }}
                >
                  {reviewHistory.statistics.totalVersions}
                </div>
                <Text type="secondary">提交版本</Text>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ textAlign: 'center' }}>
                <div
                  style={{
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: '#ff4d4f',
                  }}
                >
                  {reviewHistory.statistics.rejectedCount}
                </div>
                <Text type="secondary">驳回次数</Text>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ textAlign: 'center' }}>
                <div
                  style={{
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: '#52c41a',
                  }}
                >
                  {reviewHistory.statistics.approvedCount}
                </div>
                <Text type="secondary">通过次数</Text>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ textAlign: 'center' }}>
                <div
                  style={{
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: '#faad14',
                  }}
                >
                  {reviewHistory.statistics.averageReviewTime || 'N/A'}
                </div>
                <Text type="secondary">平均审核时间(小时)</Text>
              </div>
            </Col>
          </Row>
        </Card>

        {/* 版本历史 */}
        <Card
          title={getCardTitleStyle(<ClockCircleOutlined />, '版本历史')}
          size="small"
          style={getCardStyle()}
          styles={{ body: { padding: '16px' } }}
        >
          <Timeline>
            {reviewHistory.versions.map(version => (
              <Timeline.Item
                key={version.version}
                dot={getStatusIcon(version.status, version.reviewResult)}
                color={getStatusColor(version.status, version.reviewResult)}
              >
                <div style={{ marginBottom: '8px' }}>
                  <Space>
                    <Text strong>第{version.version}版</Text>
                    <Tag
                      color={getStatusColor(
                        version.status,
                        version.reviewResult
                      )}
                    >
                      {version.reviewResult || version.status}
                    </Tag>
                    {version.changedFieldsCount > 0 && (
                      <Tag color="blue">
                        {version.changedFieldsCount}个字段变更
                      </Tag>
                    )}
                  </Space>
                </div>

                <div style={{ color: '#666', marginBottom: '8px' }}>
                  提交时间:{' '}
                  {new Date(version.submittedAt).toLocaleString('zh-CN')}
                  {version.reviewedAt && (
                    <span style={{ marginLeft: '16px' }}>
                      审核时间:{' '}
                      {new Date(version.reviewedAt).toLocaleString('zh-CN')}
                    </span>
                  )}
                </div>

                {version.changeDescription && (
                  <div style={{ marginBottom: '8px' }}>
                    <Text type="secondary">变更说明: </Text>
                    <Text>{version.changeDescription}</Text>
                  </div>
                )}

                {version.reviewRecord && (
                  <Collapse size="small" ghost>
                    <Panel header="查看审核详情" key="review">
                      <Descriptions size="small" column={1}>
                        <Descriptions.Item label="审核人员">
                          {version.reviewRecord.reviewerName}
                        </Descriptions.Item>
                        <Descriptions.Item label="审核意见">
                          {version.reviewRecord.comments}
                        </Descriptions.Item>
                        {version.reviewRecord.rejectionReasons.length > 0 && (
                          <Descriptions.Item label="驳回原因">
                            {version.reviewRecord.rejectionReasons.map(
                              (reason, idx) => (
                                <div key={idx} style={{ marginBottom: '4px' }}>
                                  • {reason}
                                </div>
                              )
                            )}
                          </Descriptions.Item>
                        )}
                      </Descriptions>
                    </Panel>
                  </Collapse>
                )}
              </Timeline.Item>
            ))}
          </Timeline>
        </Card>
      </div>
    );
  };

  return (
    <Modal
      {...getLargeModalProps()}
      title={getUnifiedModalTitle(<EditOutlined />, '重新提交问卷')}
      open={visible}
      onCancel={onCancel}
      footer={
        activeTab === 'review'
          ? [
              <Button
                key="cancel"
                onClick={onCancel}
                style={getSecondaryButtonStyle()}
              >
                取消
              </Button>,
              <Button
                key="history"
                icon={<HistoryOutlined />}
                onClick={() => setActiveTab('history')}
                style={getCancelButtonStyle()}
              >
                查看历史
              </Button>,
              <Button
                key="resubmit"
                type="primary"
                icon={<SendOutlined />}
                loading={loading}
                onClick={handleResubmit}
                style={getPrimaryButtonStyle('primary')}
              >
                重新提交
              </Button>,
            ]
          : [
              <Button
                key="back"
                onClick={() => setActiveTab('review')}
                style={getCancelButtonStyle()}
              >
                返回修改
              </Button>,
              <Button
                key="close"
                type="primary"
                onClick={onCancel}
                style={getPrimaryButtonStyle('primary')}
              >
                关闭
              </Button>,
            ]
      }
    >
      <div style={getScrollableContentStyle('70vh')}>
        {activeTab === 'review' ? (
          <div>
            <Alert
              message="问卷需要修改"
              description="您的问卷未通过审核，请根据以下意见进行修改后重新提交。"
              type="warning"
              showIcon
              style={getAlertStyle('warning')}
            />

            {historyLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" tip="正在加载审核历史..." />
              </div>
            ) : (
              <>
                {renderLatestRejection()}

                <Card
                  title={getCardTitleStyle(<SendOutlined />, '重新提交说明')}
                  size="small"
                  style={getCardStyle()}
                  styles={{ body: { padding: '16px' } }}
                >
                  <Form form={form} layout="vertical">
                    <Form.Item
                      name="changeDescription"
                      label="变更说明"
                      rules={[
                        { required: true, message: '请填写本次变更的说明' },
                        { min: 10, message: '变更说明至少10个字符' },
                      ]}
                    >
                      <TextArea
                        rows={4}
                        placeholder="请详细说明本次修改的内容和原因，这将有助于审核人员快速了解您的改进措施..."
                        showCount
                        maxLength={500}
                      />
                    </Form.Item>

                    <Alert
                      message="提示"
                      description="填写详细的变更说明有助于加快审核速度。请说明您针对驳回意见做了哪些具体的改进。"
                      type="info"
                      showIcon
                      style={getAlertStyle('info')}
                    />
                  </Form>
                </Card>
              </>
            )}
          </div>
        ) : (
          <div>
            {historyLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" tip="正在加载审核历史..." />
              </div>
            ) : (
              renderReviewHistory()
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};
